import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import _ from 'lodash'
import { CustomCellEditorProps } from 'ag-grid-react'
import { InputBase, styled } from '@mui/material'
import DatePicker from 'react-datepicker'
import DateVO from '../../../../../vo/DateVO'
import { completeDateString } from '../../../../commons/inputSupporter'
import {
  DateVO as DomainDateVO,
  dateVoService,
} from '../../../../../domain/value-object/DateVO'
import { DatePickerCustomHeader } from '../../../../components/inputs/DatePicker/DatePickerCustomHeader'
import { PopperContainer } from '../../../../components/inputs/DatePicker'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'

type SiblingDates = {
  startDate?: string
  endDate?: string
}

export type IDateCellEditorParams = CustomCellEditorProps & {
  getInitialValueOnCalendar?: (
    params: IDateCellEditorParams
  ) => DomainDateVO | undefined
  getPreviousSiblingDates?: (
    params: IDateCellEditorParams
  ) => SiblingDates | undefined
  showYearMonthDropdown?: boolean
}
export const DateCellEditor = (props: IDateCellEditorParams) => {
  const [value, setValue] = useState<DateVO | undefined>(
    props.value ? new DateVO(props.value) : undefined
  )
  const initialValueOnCalendar = props.getInitialValueOnCalendar
    ? props.getInitialValueOnCalendar(props)
    : undefined

  const siblingDates = props.getPreviousSiblingDates
    ? props.getPreviousSiblingDates(props)
    : undefined

  const [calendarKey, setCalendarKey] = useState(0)
  const calendarSelected = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (calendarSelected.current) {
      props.api.stopEditing()
      props.api.setFocusedCell(props.rowIndex, props.column)
    }
  }, [value])

  const onChange = useCallback((date: Date | undefined) => {
    const newValue = date ? new DateVO(date) : undefined
    setValue(newValue)
    props.onValueChange(newValue?.serialize())
  }, [])

  const onSelect = useCallback((date: Date | undefined) => {
    const newValue = date ? new DateVO(date) : undefined
    setValue(newValue)
    props.onValueChange(newValue?.serialize())
    calendarSelected.current = true
  }, [])

  const onChangeCapture = useCallback(
    _.debounce(event => {
      const inputValue = event.target.value

      if (!inputValue) {
        onChange(undefined)
      }
      const value = completeDateString(inputValue)
      if (!value) {
        return
      }
      const newValue = new Date(value.replace(/\//g, '-'))
      onChange(newValue)
    }, 200),
    [onChange]
  )

  const fillFromSibling = useCallback((dateStr: string) => {
    const newValue = new DateVO(dateStr)
    setValue(newValue)
    props.onValueChange(newValue.serialize())
    setCalendarKey(k => k + 1)
  }, [])

  const { startDate: siblingStartDate, endDate: siblingEndDate } =
    siblingDates ?? {}

  // 関数の同一性が変わるとカレンダーが再マウントされるため memo 化する
  const calendarContainer = useMemo(() => {
    if (!siblingStartDate && !siblingEndDate) return undefined
    return ({ className, children }: CalendarContainerProps) => (
      <PreviousRowCalendarContainer
        className={className}
        startDate={siblingStartDate}
        endDate={siblingEndDate}
        onFill={fillFromSibling}
      >
        {children}
      </PreviousRowCalendarContainer>
    )
  }, [siblingStartDate, siblingEndDate, fillFromSibling])

  return (
    <DatePicker
      key={calendarKey}
      open={true}
      selected={
        value?.toNumberValue() ||
        (initialValueOnCalendar
          ? dateVoService.toNumberValue(initialValueOnCalendar)
          : undefined)
      }
      dateFormat={'MM/dd'}
      onSelect={onSelect}
      customInput={
        <InputBase
          inputRef={inputRef}
          autoFocus={true}
          value={value?.format()}
          onChangeCapture={onChangeCapture}
        />
      }
      fixedHeight={true}
      popperContainer={PopperContainer}
      calendarContainer={calendarContainer}
      renderCustomHeader={
        props.showYearMonthDropdown ? DatePickerCustomHeader : undefined
      }
    />
  )
}

type CalendarContainerProps = {
  className: string
  children: ReactNode
}

type PreviousRowCalendarContainerProps = CalendarContainerProps & {
  startDate?: string
  endDate?: string
  onFill: (date: string) => void
}

const PreviousRowCalendarContainer = ({
  className,
  children,
  startDate,
  endDate,
  onFill,
}: PreviousRowCalendarContainerProps) => (
  <div className={className + ' ag-custom-component-popup'}>
    <PreviousRowFillArea>
      <PreviousRowCaption>
        {intl.formatMessage({ id: 'dateCellEditor.previousRow.caption' })}
      </PreviousRowCaption>
      <PreviousRowButtonRow>
        <PreviousRowFillButton
          messageId="dateCellEditor.previousRow.startDate"
          date={startDate}
          onFill={onFill}
        />
        <PreviousRowFillButton
          messageId="dateCellEditor.previousRow.endDate"
          date={endDate}
          onFill={onFill}
        />
      </PreviousRowButtonRow>
    </PreviousRowFillArea>
    <CalendarBody>{children}</CalendarBody>
  </div>
)

type PreviousRowFillButtonProps = {
  messageId: string
  date?: string
  onFill: (date: string) => void
}

const PreviousRowFillButton = ({
  messageId,
  date,
  onFill,
}: PreviousRowFillButtonProps) => (
  <PreviousRowButton disabled={!date} onClick={() => date && onFill(date)}>
    {intl.formatMessage({ id: messageId })}
    <PreviousRowDate>{formatSiblingDate(date)}</PreviousRowDate>
  </PreviousRowButton>
)

const NO_DATE_PLACEHOLDER = '—'

const formatSiblingDate = (dateStr?: string) =>
  dateStr ? new DateVO(dateStr).format('M/D') : NO_DATE_PLACEHOLDER

const CalendarBody = styled('div')({
  position: 'relative',
})

const PreviousRowFillArea = styled('div')({
  borderBottom: `1px solid ${colorPalette.monotone[1]}`,
})

const PreviousRowCaption = styled('div')({
  fontSize: 10,
  color: colorPalette.monotone[4],
  padding: '8px 10px 3px',
})

const PreviousRowButtonRow = styled('div')({
  display: 'flex',
  gap: 4,
  padding: '0 10px 8px',
})

const PreviousRowButton = styled('button')({
  flex: 1,
  minWidth: 0,
  height: 34,
  padding: 0,
  borderRadius: 4,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  fontSize: 11,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  background: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  color: colorPalette.monotone[4],
  '&:hover:not(:disabled)': {
    border: `1px solid ${colorPalette.skyBlue[7]}`,
    color: colorPalette.skyBlue[8],
  },
  '&:disabled': {
    background: colorPalette.monotone[1],
    border: `1px solid ${colorPalette.monotone[2]}`,
    color: colorPalette.monotone[2],
    cursor: 'default',
  },
})

const PreviousRowDate = styled('span')({
  fontWeight: 600,
})
